import { useMemo } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import type { SdmsCheckPointEdit } from '../SdmsValidationActions';
import type { PlmCheckPoint } from './plm.api';
import { effectiveCheckPoint, unansweredCheckPoints } from './plmWorkflow';

export interface PlmCheckPointsTabProps {
  rows: PlmCheckPoint[];
  loading: boolean;
  /** Unsaved answers by check point id — saved just before Validate. */
  edits: Map<string, SdmsCheckPointEdit>;
  onEdit: (id: string, patch: Partial<SdmsCheckPointEdit>) => void;
  readOnly: boolean;
}

/**
 * Validate mode's Check Points tab (owner decision 2026-09-29: "Answer them in
 * PLM Validate"). The batch's data-package check points
 * (dbo.DataPackageChecklistQC), answered here and saved through the SHARED SDMS
 * save-edits endpoint just before Validate — the same rows, the same rule and
 * the same write as SDMS Result Validation's Check Points step, so a PLM batch
 * can be validated without leaving PLM. Batch-wide, like the QC Result View.
 */
export function PlmCheckPointsTab({ rows, loading, edits, onEdit, readOnly }: PlmCheckPointsTabProps) {
  const columns = useMemo<AppColumn<PlmCheckPoint>[]>(() => [
    { field: 'checkPoint', header: 'Check Point', width: 360, text: r => r.checkPoint ?? '' },
    {
      field: 'decision', header: 'Answer', width: 120,
      text: r => effectiveCheckPoint(r, edits.get(r.id)).decision,
      render: r => (
        <select className="plm-input" aria-label={`Answer — ${r.checkPoint ?? ''}`} disabled={readOnly}
          value={effectiveCheckPoint(r, edits.get(r.id)).decision}
          onChange={e => onEdit(r.id, { decision: e.target.value || null })}>
          <option value="">—</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="NA">N/A</option>
        </select>
      ),
    },
    {
      field: 'qualifier', header: 'Qualifier', width: 140,
      text: r => effectiveCheckPoint(r, edits.get(r.id)).qualifier,
      render: r => (
        <input className="plm-input" aria-label={`Qualifier — ${r.checkPoint ?? ''}`} disabled={readOnly}
          value={effectiveCheckPoint(r, edits.get(r.id)).qualifier}
          onChange={e => onEdit(r.id, { qualifier: e.target.value })} />
      ),
    },
    {
      field: 'comment', header: 'Comment', width: 260,
      text: r => effectiveCheckPoint(r, edits.get(r.id)).comment,
      render: r => (
        <input className="plm-input" aria-label={`Comment — ${r.checkPoint ?? ''}`} disabled={readOnly}
          value={effectiveCheckPoint(r, edits.get(r.id)).comment}
          onChange={e => onEdit(r.id, { comment: e.target.value })} />
      ),
    },
  ], [edits, onEdit, readOnly]);

  const open = unansweredCheckPoints(rows, edits);
  return (
    <div className="plm-checkpoints">
      <p className="plm-hint">
        {rows.length === 0
          ? 'This batch’s data package has no check points — nothing to answer before validating.'
          : open > 0
            ? `${open} of ${rows.length} check point(s) still need Yes / No / N/A — Validate is refused until every one is answered.`
            : `All ${rows.length} check point(s) answered. Your answers are saved when you press Validate.`}
      </p>
      {rows.length > 0 && (
        <AppDataGrid
          rows={rows}
          columns={columns}
          getRowId={r => r.id}
          loading={loading}
          defaultPageSize={20}
          maximizable
          exportFilename="PLM-Check-Points"
          emptyText="No check points."
        />
      )}
    </div>
  );
}
